import React, { useState } from 'react';
import { 
  Plus, 
  Search, 
  BookOpen, 
  Filter,
  RefreshCw,
  AlertCircle,
  Loader,
 GraduationCap
} from 'lucide-react';
import useResources from '../hooks/useResources';
import ResourceCard from '../components/ResourceCard';
import ResourceFilters from '../components/ResourceFilters';
import ResourceUploadModal from '../components/ResourceUploadModal';

const ResourcesPage = () => {
  const {
    resources,
    loading,
    uploading,
    subjects,
    universities,
    filters,
    pagination,
    error,
    userUniversity,
    fetchResources,
    loadMore,
    uploadResource,
    likeResource,
    dislikeResource,
    deleteResource,
    incrementViews,
    updateFilters,
    resetFilters,
    getUniversityFilterDescription
  } = useResources();

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Filter resources by search term
  const filteredResources = resources.filter(resource =>
    resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    resource.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    resource.subject.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleUploadSuccess = (resourceData, file) => {
    return uploadResource(resourceData, file);
  };

  const handleResourceView = (resourceId) => {
    incrementViews(resourceId);
  };

  const handleRefresh = () => {
    fetchResources(true);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="mb-4 lg:mb-0">
              <div className="flex items-center mb-2">
                <BookOpen className="w-6 h-6 text-green-500 mr-2" />
                <h1 className="text-2xl font-bold text-gray-900">Academic Resources</h1>
              </div>
              <p className="text-gray-600">
                Discover and share educational resources with fellow students
              </p>
              {userUniversity && (
                <div className="flex items-center mt-2 text-sm text-gray-500">
                  <GraduationCap className="w-4 h-4 mr-1" />
                  <span>Your University: <span className="font-medium text-gray-700">{userUniversity}</span></span>
                </div>
              )}
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              
              <button
                onClick={() => setShowUploadModal(true)}
                className="flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" />
                Upload Resource
              </button>
            </div>
          </div>
        </div>

        {/* Search and Filter Controls */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search resources by title, description, or subject..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center px-4 py-2 text-sm font-medium border rounded-lg transition-colors ${
                showFilters
                  ? 'bg-green-50 border-green-200 text-green-700'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <ResourceFilters
            filters={filters}
            subjects={subjects}
            universities={universities}
            userUniversity={userUniversity}
            onFiltersChange={updateFilters}
            onReset={resetFilters}
            resourceCount={filteredResources.length}
            getUniversityFilterDescription={getUniversityFilterDescription}
          />
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
              <p className="text-red-700">{error}</p>
              <button
                onClick={handleRefresh}
                className="ml-auto text-red-600 hover:text-red-700 font-medium"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Resources Grid */}
        {loading && resources.length === 0 ? (
          <div className="flex justify-center items-center py-12">
            <div className="text-center">
              <Loader className="w-8 h-8 text-green-500 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Loading resources...</p>
            </div>
          </div>
        ) : filteredResources.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? 'No resources found' : 'No resources available'}
            </h3>
            <p className="text-gray-600 mb-6">
              {searchTerm 
                ? 'Try adjusting your search terms or filters'
                : userUniversity
                  ? `Be the first to share an educational resource for ${userUniversity}`
                  : 'Be the first to share an educational resource with your peers'
              }
            </p>
            {!searchTerm && (
              <button
                onClick={() => setShowUploadModal(true)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
              >
                <Plus className="w-4 h-4 mr-2" />
                Upload First Resource
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Results Summary */}
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Showing {filteredResources.length} of {resources.length} resources
                {searchTerm && (
                  <span className="ml-1">
                    for "{searchTerm}"
                  </span>
                )}
              </p>
              {getUniversityFilterDescription && filters.universityFilter !== 'smart' && (
                <p className="text-xs text-gray-500 mt-1">
                  {getUniversityFilterDescription()}
                </p>
              )}
            </div>

            {/* Resources Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
              {filteredResources.map((resource) => (
                <ResourceCard
                  key={resource.id}
                  resource={resource}
                  onLike={likeResource}
                  onDislike={dislikeResource}
                  onDelete={deleteResource}
                  onView={handleResourceView}
                />
              ))}
            </div>

            {/* Load More */}
            {pagination.hasMore && !searchTerm && (
              <div className="text-center">
                <button
                  onClick={loadMore}
                  disabled={loading}
                  className="inline-flex items-center px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    'Load More Resources'
                  )}
                </button>
              </div>
            )}
          </>
        )}

        {/* Upload Modal */}
        <ResourceUploadModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onUpload={handleUploadSuccess}
          uploading={uploading}
          subjects={subjects}
        />
      </div>
    </div>
  );
};

export default ResourcesPage;